import { useCallback, useMemo } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import { useLeadResponsibleChange } from '@app/components/LeadCard/model/useLeadResponsibleChange';
import { useReplaceLatestRegistryLead } from '@app/pages/Leads/model';

import { toResponsibleEmployees } from '../utils';

import type { LeadsRegistryAssigneeOption } from '../model/types';

interface UseLeadsRegistryResponsibleParams {
  lead: ModelLead;
  options: LeadsRegistryAssigneeOption[];
}

export const useLeadsRegistryResponsible = ({
  lead,
  options,
}: UseLeadsRegistryResponsibleParams) => {
  const replaceLead = useReplaceLatestRegistryLead();

  const employees = useMemo(
    () => toResponsibleEmployees(options, lead),
    [lead, options]
  );

  const responsibleChangeSuccessHandler = useCallback(
    ({
      responsibleUserId,
      updatedLead,
    }: {
      responsibleUserId: number;
      updatedLead: ModelLead | null;
    }) => {
      const responsibleName =
        employees.find(({ id }) => Number(id) === responsibleUserId)?.name ??
        updatedLead?.UserName ??
        null;

      replaceLead(
        updatedLead ?? {
          ...lead,
          UserId: responsibleUserId,
          UserName: responsibleName,
        }
      );
    },
    [employees, lead, replaceLead]
  );

  const {
    changeResponsible,
    hasRightToChangeResponsible,
    isResponsibleChanging,
  } = useLeadResponsibleChange({
    leadId: lead.Id,
    onSuccess: responsibleChangeSuccessHandler,
  });

  return {
    employees,
    selectedEmployeeId: lead.UserId,
    isResponsibleChanging,
    isResponsibleChangeDisabled: !hasRightToChangeResponsible,
    changeResponsible,
  };
};
